import { useEffect, useState, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { LoadingState, ErrorState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Cpu, Plus, Loader2, CheckCircle2, XCircle, AlertTriangle, PlugZap, Trash2, ArrowRight, Ban,
} from "lucide-react";

const inputCls = "bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-violet-500/30";

const STATE_UI = {
  available: { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", Icon: CheckCircle2, label: "Available" },
  unverified: { cls: "bg-amber-500/10 text-amber-300 border-amber-500/30", Icon: AlertTriangle, label: "Unverified" },
  "key-required": { cls: "bg-sky-500/10 text-sky-300 border-sky-500/30", Icon: PlugZap, label: "Key required" },
  failed: { cls: "bg-rose-500/10 text-rose-300 border-rose-500/30", Icon: XCircle, label: "Failed" },
  "unavailable-not-public": { cls: "bg-rose-500/10 text-rose-300 border-rose-500/30", Icon: Ban, label: "Not public" },
  "not-installed": { cls: "bg-slate-700/40 text-slate-400 border-slate-600/40", Icon: XCircle, label: "Not installed" },
  planned: { cls: "bg-violet-500/10 text-violet-300 border-violet-500/30", Icon: AlertTriangle, label: "Planned" },
  disconnected: { cls: "bg-slate-700/40 text-slate-400 border-slate-600/40", Icon: XCircle, label: "Disconnected" },
  disabled: { cls: "bg-slate-700/40 text-slate-500 border-slate-600/40", Icon: XCircle, label: "Disabled" },
  deprecated: { cls: "bg-rose-500/10 text-rose-300 border-rose-500/30", Icon: XCircle, label: "Deprecated" },
};

function StatePill({ state }) {
  const s = STATE_UI[state] || STATE_UI.unverified;
  return <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${s.cls}`} data-testid={`state-${state}`}><s.Icon className="w-3 h-3" /> {s.label}</span>;
}

function AddEntry({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", provider: "manual", model_id_or_endpoint: "", capabilities: "director" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim() || !f.model_id_or_endpoint.trim()) { toast.error("Name and model id/endpoint required"); return; }
    setBusy(true);
    try {
      await api.post("/registry", { ...f, capabilities: f.capabilities.split(",").map((x) => x.trim()).filter(Boolean), modalities: ["text"] });
      toast.success("Entry added (unverified — Verify to activate)");
      setOpen(false); setF({ name: "", provider: "manual", model_id_or_endpoint: "", capabilities: "director" }); onAdded();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="add-entry"><Plus className="w-4 h-4 mr-1.5" /> Add model</Button></DialogTrigger>
      <DialogContent className="bg-[#111827] border-slate-800 text-slate-100">
        <DialogHeader><DialogTitle>Add manual model</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-slate-300 text-sm mb-1 block">Name</Label><Input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="entry-name" /></div>
          <div><Label className="text-slate-300 text-sm mb-1 block">Provider</Label><Input className={inputCls} value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} placeholder="emergent | openai_user | custom" data-testid="entry-provider" /></div>
          <div><Label className="text-slate-300 text-sm mb-1 block">Model id / endpoint</Label><Input className={inputCls} value={f.model_id_or_endpoint} onChange={(e) => setF({ ...f, model_id_or_endpoint: e.target.value })} data-testid="entry-model" /></div>
          <div><Label className="text-slate-300 text-sm mb-1 block">Capabilities (comma)</Label><Input className={inputCls} value={f.capabilities} onChange={(e) => setF({ ...f, capabilities: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="entry-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ModelRegistry() {
  const [items, setItems] = useState(null);
  const [effective, setEffective] = useState([]);
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const load = async () => {
    setError(null);
    try {
      const [a, b] = await Promise.all([api.get("/registry"), api.get("/registry/effective-roles")]);
      setItems(a.data); setEffective(b.data);
    } catch (e) { setError(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const verify = async (id) => {
    setVerifying(id);
    try {
      const { data } = await api.post(`/registry/${id}/verify`);
      data.ok ? toast.success(`Verified — available (${data.latency_ms}ms)`) : toast.error(`Verify failed: ${(data.message || "").slice(0, 140)}`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setVerifying(null); }
  };
  const del = async (id) => { try { await api.delete(`/registry/${id}`); toast.success("Deleted"); await load(); } catch (e) { toast.error(apiError(e)); } };

  const roles = useMemo(() => Array.from(new Set((items || []).map((e) => e.intended_role).filter(Boolean))).sort((a, b) => a.localeCompare(b)), [items]);
  const shown = useMemo(() => (items || []).filter((e) => roleFilter === "all" || e.intended_role === roleFilter), [items, roleFilter]);
  const effById = useMemo(() => Object.fromEntries(effective.map((e) => [e.id, e])), [effective]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!items) return <LoadingState label="Loading registry…" />;

  return (
    <div className="animate-in-up">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1 flex items-center gap-1.5"><Cpu className="w-3.5 h-3.5" /> Workspace</p>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">Model &amp; Tool Registry</h1>
          <p className="text-sm text-slate-400 mt-2">Single source of truth. A model is only "available" after a real verification. Not-public models can never flip to available.</p>
        </div>
        <AddEntry onAdded={load} />
      </div>

      {/* Effective role → model mapping (real resolution logic) */}
      {effective.length > 0 && (
        <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5 mb-6" data-testid="effective-mapping">
          <h2 className="text-sm font-display font-bold text-slate-100 mb-3">Effective role → model mapping</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {effective.map((e) => (
              <div key={e.id} className="flex items-center gap-2 text-xs bg-slate-900/50 border border-slate-800 rounded-lg px-3 py-2" data-testid={`effective-${e.intended_role}`}>
                <span className="text-slate-500 w-32 flex-none truncate">{e.intended_role}</span>
                <span className="font-mono text-slate-300 truncate">{e.requested_model}</span>
                <ArrowRight className="w-3 h-3 text-slate-600 flex-none" />
                {e.effective_model
                  ? <span className={`font-mono truncate ${e.using_fallback ? "text-amber-300" : "text-emerald-400"}`}>{e.effective_model}<span className="text-slate-500 ml-1">({e.source})</span></span>
                  : <span className="text-rose-400">no usable model</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-60 bg-slate-900 border-slate-800 text-slate-200" data-testid="role-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
            <SelectItem value="all">All roles ({items.length})</SelectItem>
            {roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500">{shown.length} shown</span>
      </div>

      <div className="space-y-3" data-testid="registry-list">
        {shown.map((e) => {
          const eff = effById[e.id];
          return (
            <div key={e.id} className="bg-[#111827] border border-slate-800/80 rounded-xl p-4" data-testid={`registry-${e.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-100">{e.name}</span>
                    <StatePill state={e.state} />
                    {e.intended_role && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700" data-testid={`role-tag-${e.id}`}>{e.intended_role}</span>}
                    {e.usable && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300" data-testid={`usable-${e.id}`}>in pickers</span>}
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">{e.source}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 font-mono">{e.model_id_or_endpoint}</p>
                  {eff && eff.using_fallback && (
                    <p className="text-[11px] text-amber-300 mt-1" data-testid={`fallback-active-${e.id}`}>serving via fallback → <span className="font-mono">{eff.active_fallback}</span></p>
                  )}
                  {e.fallback_chain && e.fallback_chain.length > 0 && (
                    <p className="text-[11px] text-slate-600 mt-0.5">fallback chain: {e.fallback_chain.join(" → ")}</p>
                  )}
                  {e.last_error && <p className="text-[11px] text-rose-400/80 mt-0.5 break-all">{e.last_error}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={() => verify(e.id)} disabled={verifying === e.id} size="sm" className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700" data-testid={`verify-${e.id}`}>
                    {verifying === e.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PlugZap className="w-4 h-4 mr-1.5" /> Verify</>}
                  </Button>
                  {e.source !== "builtin" && e.source !== "spec" && <Button onClick={() => del(e.id)} size="sm" variant="ghost" className="text-rose-400 hover:bg-slate-800" data-testid={`del-${e.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
